import React, { useEffect, useState } from 'react';
import { useApp } from '../../AppContext';
import { EMAIL_VARIABLES, EmailTemplateItem } from '../../types';

const TRIGGER_LABELS: Record<EmailTemplateItem['trigger'], { title: string; hint: string }> = {
  Confermato: { title: 'Email su Conferma', hint: 'Inviata quando accetti una prenotazione.' },
  Rifiutato: { title: 'Email su Rifiuto', hint: 'Inviata quando rifiuti una prenotazione.' },
  Completato: { title: 'Email su Completamento', hint: 'Inviata quando segni l\'appuntamento come completato.' },
};

export default function EmailTemplates() {
  const { emailTemplates } = useApp();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-serif text-xl">Notifiche email al cliente</h3>
        <p className="text-xs text-sage mt-1">
          Attiva e personalizza le email automatiche che il cliente riceve quando cambi lo stato di una prenotazione.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {emailTemplates
          .slice()
          .sort((a, b) => a.trigger.localeCompare(b.trigger))
          .map(t => <TemplateCard key={t.id} template={t} />)}
      </div>

      <div className="bg-forestDark/40 rounded-2xl border border-sage/15 p-4">
        <div className="text-xs uppercase tracking-wider text-sage font-bold mb-2">Variabili disponibili</div>
        <div className="flex flex-wrap gap-2">
          {EMAIL_VARIABLES.map(v => (
            <code key={v} className="bg-forest text-cream px-2 py-1 rounded text-[11px]">{v}</code>
          ))}
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template }: { template: EmailTemplateItem }) {
  const { updateEmailTemplate, showToast } = useApp();
  const [enabled, setEnabled] = useState(template.enabled);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEnabled(template.enabled); setSubject(template.subject); setBody(template.body); }, [template]);

  const dirty = enabled !== template.enabled || subject !== template.subject || body !== template.body;

  const label = TRIGGER_LABELS[template.trigger];

  const toggle = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(template.id, { enabled: !enabled });
      setEnabled(!enabled);
    } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(template.id, { enabled, subject, body });
      showToast(`Template "${template.trigger}" salvato.`);
    } catch { /* toast in context */ }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-forest p-5 rounded-3xl border border-sage/15 space-y-3 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-serif text-lg">{label.title}</div>
          <div className="text-[11px] text-sage">{label.hint}</div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${enabled ? 'bg-emerald-600' : 'bg-stone-500/40'} disabled:opacity-60`}
          title={enabled ? 'Disattiva' : 'Attiva'}
        >
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-cream transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] uppercase tracking-wider font-bold text-sage">Oggetto</label>
        <input
          type="text"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          className="w-full bg-forestDark/55 border border-sage/20 rounded-lg px-3 py-2 text-sm text-cream focus:outline-none focus:border-sage"
        />
      </div>

      <div className="space-y-1 flex-1 flex flex-col">
        <label className="text-[11px] uppercase tracking-wider font-bold text-sage">Corpo</label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={9}
          className="w-full flex-1 bg-forestDark/55 border border-sage/20 rounded-lg p-3 text-sm text-cream font-mono focus:outline-none focus:border-sage resize-y min-h-[180px]"
        />
      </div>

      <div className="flex items-center justify-end pt-1">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-sage hover:bg-sageLight text-forestDark px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold smooth-transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'Salvataggio…' : dirty ? 'Salva modifiche' : '✓ Salvato'}
        </button>
      </div>
    </div>
  );
}
